import { isAppAdmin } from '@ordo/auth'
import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ordo/ui/card'
import { Checkbox } from '@ordo/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@ordo/ui/dialog'
import { Input } from '@ordo/ui/input'
import { Label } from '@ordo/ui/label'
import { Skeleton } from '@ordo/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ordo/ui/table'
import { LoadingWrapper } from 'compositions/LoadingWrapper'
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from 'hooks/api'
import { Check, Copy, Plus, Trash2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useAuthStore } from 'stores/auth'
import { API_KEY_RESOURCES, type ApiKeyResource, type ApiKeyScope } from 'types/api-keys'

const READ: ApiKeyScope[] = API_KEY_RESOURCES.map((r) => `${r}:read` as ApiKeyScope)
const WRITE: ApiKeyScope[] = API_KEY_RESOURCES.map((r) => `${r}:write` as ApiKeyScope)

function emptyScopeSet(): Set<ApiKeyScope> {
  return new Set<ApiKeyScope>()
}

export function ApiKeysSection() {
  const user = useAuthStore((s) => s.user)
  const { data: apiKeys, loading, refetch } = useApiKeys()
  const { perform: createKey, loading: creating } = useCreateApiKey()
  const { perform: revokeKey } = useRevokeApiKey()

  const canGrantAdmin = isAppAdmin(user, 'comms')

  const [createOpen, setCreateOpen] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [scopes, setScopes] = useState<Set<ApiKeyScope>>(emptyScopeSet)
  const [grantAdmin, setGrantAdmin] = useState(false)
  const [revealedKey, setRevealedKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const toggleScope = useCallback((scope: ApiKeyScope) => {
    setScopes((prev) => {
      const next = new Set(prev)
      if (next.has(scope)) {
        next.delete(scope)
      } else {
        next.add(scope)
      }
      return next
    })
  }, [])

  const toggleAllRead = useCallback(() => {
    setScopes((prev) => {
      const next = new Set(prev)
      const hasAll = READ.every((s) => next.has(s))
      for (const s of READ) {
        if (hasAll) {
          next.delete(s)
        } else {
          next.add(s)
        }
      }
      return next
    })
  }, [])

  const toggleAllWrite = useCallback(() => {
    setScopes((prev) => {
      const next = new Set(prev)
      const hasAll = WRITE.every((s) => next.has(s))
      for (const s of WRITE) {
        if (hasAll) {
          next.delete(s)
        } else {
          next.add(s)
        }
      }
      return next
    })
  }, [])

  const handleCreate = useCallback(async () => {
    if (!user || !newKeyName.trim()) {
      return
    }
    const result = await createKey({
      name: newKeyName.trim(),
      scopes: Array.from(scopes),
      isAdmin: canGrantAdmin && grantAdmin,
    })
    setRevealedKey(result.key)
    refetch()
  }, [user, newKeyName, scopes, grantAdmin, canGrantAdmin, createKey, refetch])

  const handleCloseCreate = useCallback(() => {
    setCreateOpen(false)
    setRevealedKey(null)
    setNewKeyName('')
    setScopes(emptyScopeSet())
    setGrantAdmin(false)
    setCopied(false)
  }, [])

  const handleCopy = useCallback(async () => {
    if (revealedKey) {
      await navigator.clipboard.writeText(revealedKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [revealedKey])

  const handleRevoke = useCallback(
    async (id: string) => {
      await revokeKey(id)
      refetch()
    },
    [revokeKey, refetch],
  )

  if (!user) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>API Keys</CardTitle>
            <CardDescription>Manage API keys for authenticating requests.</CardDescription>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4" />
                Create Key
              </Button>
            </DialogTrigger>
            <DialogContent>
              {revealedKey ? (
                <>
                  <DialogHeader>
                    <DialogTitle>API Key Created</DialogTitle>
                    <DialogDescription>Copy your key now. You will not be able to see it again.</DialogDescription>
                  </DialogHeader>
                  <div className="flex min-w-0 items-center gap-2">
                    <code className="min-w-0 flex-1 truncate rounded bg-muted px-3 py-2 text-sm">{revealedKey}</code>
                    <Button variant="outline" size="icon" onClick={handleCopy}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    {copied ? <p className="text-sm text-muted-foreground">Copied to clipboard</p> : <div />}
                    <Button onClick={handleCloseCreate}>Done</Button>
                  </div>
                </>
              ) : (
                <>
                  <DialogHeader>
                    <DialogTitle>Create API Key</DialogTitle>
                    <DialogDescription>
                      Pick a name and the scopes this key should grant. Only callers presenting the key with the
                      matching scope can hit each endpoint.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-2">
                    <Label htmlFor="api-key-name">Name</Label>
                    <Input
                      id="api-key-name"
                      placeholder="e.g. road-warrior"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Scopes</Label>
                      <div className="flex gap-2 text-xs">
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={toggleAllRead}>
                          all read
                        </button>
                        <span className="text-muted-foreground">·</span>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={toggleAllWrite}>
                          all write
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 gap-y-2 text-sm">
                      <div />
                      <div className="text-center text-xs text-muted-foreground">Read</div>
                      <div className="text-center text-xs text-muted-foreground">Write</div>
                      {API_KEY_RESOURCES.map((resource) => {
                        const readScope = `${resource}:read` as ApiKeyScope
                        const writeScope = `${resource}:write` as ApiKeyScope
                        return (
                          <ScopeRow
                            key={resource}
                            resource={resource}
                            readChecked={scopes.has(readScope)}
                            writeChecked={scopes.has(writeScope)}
                            onToggleRead={() => toggleScope(readScope)}
                            onToggleWrite={() => toggleScope(writeScope)}
                          />
                        )
                      })}
                    </div>
                  </div>
                  {canGrantAdmin && (
                    <div className="flex items-start gap-2 rounded-md border border-dashed p-3">
                      <Checkbox
                        id="api-key-admin"
                        checked={grantAdmin}
                        onCheckedChange={(v) => setGrantAdmin(v === true)}
                      />
                      <div className="space-y-1">
                        <Label htmlFor="api-key-admin" className="text-sm font-medium">
                          Admin
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Grants access to admin-only routes (app config, imports, audits). Does not bypass scope checks
                          above — pick those too.
                        </p>
                      </div>
                    </div>
                  )}
                  <DialogFooter>
                    <Button variant="outline" onClick={handleCloseCreate}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreate} disabled={creating || !newKeyName.trim()}>
                      Create
                    </Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <LoadingWrapper
          loading={loading}
          loadingElement={
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          }>
          {!apiKeys?.length ? (
            <p className="text-sm text-muted-foreground">No API keys yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Scopes</TableHead>
                  <TableHead>Admin</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell>
                      <code className="text-sm text-muted-foreground">{key.prefix}...</code>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {key.scopes.length === 0 ? (
                          <span className="text-xs text-muted-foreground">none</span>
                        ) : (
                          key.scopes.map((s) => (
                            <Badge key={s} variant="secondary" className="text-xs">
                              {s}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{key.isAdmin ? <Check className="h-4 w-4 text-primary" /> : null}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString() : 'Never'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(key.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleRevoke(key.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </LoadingWrapper>
      </CardContent>
    </Card>
  )
}

interface ScopeRowProps {
  resource: ApiKeyResource
  readChecked: boolean
  writeChecked: boolean
  onToggleRead: () => void
  onToggleWrite: () => void
}

function ScopeRow({ resource, readChecked, writeChecked, onToggleRead, onToggleWrite }: ScopeRowProps) {
  return (
    <>
      <Label htmlFor={`scope-${resource}-read`} className="capitalize">
        {resource}
      </Label>
      <div className="flex justify-center">
        <Checkbox id={`scope-${resource}-read`} checked={readChecked} onCheckedChange={onToggleRead} />
      </div>
      <div className="flex justify-center">
        <Checkbox id={`scope-${resource}-write`} checked={writeChecked} onCheckedChange={onToggleWrite} />
      </div>
    </>
  )
}
